import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Home, Users } from "lucide-react";
import { Link } from "react-router-dom";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-3 py-2 text-sm";

export default function CellPostcodeGroups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("most");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/cell/responses"); setRows(data.responses || []); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setLoading(false);
    })();
  }, []);

  const groups = useMemo(() => {
    const byArea = {};
    rows.forEach((r) => {
      const area = r.outward || "No postcode";
      if (!byArea[area]) byArea[area] = { area, total: 0, hosts: 0, sectors: {} };
      byArea[area].total += 1;
      if (r.willing_to_host || r.host_status === "Confirmed") byArea[area].hosts += 1;
      const sec = r.sector || area;
      byArea[area].sectors[sec] = (byArea[area].sectors[sec] || 0) + 1;
    });
    let list = Object.values(byArea);
    if (sort === "alpha") list.sort((a, b) => a.area.localeCompare(b.area));
    else if (sort === "hosts") list.sort((a, b) => b.hosts - a.hosts);
    else list.sort((a, b) => b.total - a.total);
    return list;
  }, [rows, sort]);

  return (
    <div className="space-y-6" data-testid="cell-postcode-groups-page">
      <div className="sj-page-hero flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold" style={{ color: "var(--sj-text)" }}>Postcode groups</h1>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>Counts, hosting interest and contact readiness by outward postcode.</p>
        </div>
        <select data-testid="cell-postcodes-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={inputCls}>
          <option value="most">Most responses</option>
          <option value="hosts">Most hosts</option>
          <option value="alpha">A–Z</option>
        </select>
      </div>

      {loading ? (
        <div className={`${CARD} p-8 text-center`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
      ) : groups.length === 0 ? (
        <div className={`${CARD} p-8 text-center`} style={{ color: "var(--sj-text-3)" }}>No responses yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cell-postcode-cards">
          {groups.map((g) => (
            <div key={g.area} data-testid={`cell-postcode-card-${g.area}`} className={`${CARD} p-5`}>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
                <h3 className="font-head text-xl font-semibold">{g.area}</h3>
              </div>
              <div className="flex gap-4 mt-3 text-sm">
                <span className="inline-flex items-center gap-1" style={{ color: "var(--sj-text-2)" }}><Users className="h-4 w-4" style={{ color: "var(--sj-text-3)" }} /> {g.total} responses</span>
                <span className="inline-flex items-center gap-1" style={{ color: "var(--sj-text-2)" }}><Home className="h-4 w-4" style={{ color: "var(--sj-text-3)" }} /> {g.hosts} hosts</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {Object.entries(g.sectors).sort((a, b) => b[1] - a[1]).map(([sec, n]) => (
                  <span key={sec} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#EDF2F4", color: "var(--sj-text-2)" }}>{sec} · {n}</span>
                ))}
              </div>
              <Link to={`/cell/responses?area=${encodeURIComponent(g.area)}`} data-testid={`cell-postcode-view-${g.area}`} className="mt-4 inline-block text-sm font-medium" style={{ color: "var(--sj-primary)" }}>View responses →</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
